import { FC } from 'react';
import { IonButton, useIonAlert } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { Text } from '../components/Text';
import { TextField } from '../components/TextField';

// Read-only block shown in place of the editable EmailField when a member is linked to an
// Innerhive user account. Renders the linked account email in a grayed-out, non-interactive
// box (the same read-only treatment Account Settings uses for the account email) so it matches
// the other field boxes, followed by an advisory callout — naming the account by name and email
// and explaining that edits here sync to that account across all care maps — and an Unlink button.
// Unlink confirms, then hands off to the parent form's onUnlink, which clears the email contact
// method and persists — the server then drops linkedUserId and the form re-renders with the
// editable EmailField.
//
// The read-only email box must NOT use name="email-field" — that name identifies the editable
// EmailField, and the linked/unlinked E2E assertions distinguish the two states by its absence.

interface ILinkedMemberEmailFieldProps {
    email: string;
    linkedToName: string;
    onUnlink: () => void;
    // The linked member IS the current user (the "Edit Your Info" forms). Switches the copy to
    // second person ("your account", "you're in") instead of naming them in third person.
    isSelf?: boolean;
}

const useStyles = createUseStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    readOnlyField: {
        opacity: 0.6,
        pointerEvents: 'none'
    },
    advisory: {
        background: 'rgba(17,120,157,0.08)',
        border: '1px solid rgba(17,120,157,0.18)',
        borderRadius: 10,
        padding: '10px 8px 10px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 4
    }
});

export const LinkedMemberEmailField: FC<ILinkedMemberEmailFieldProps> = ({ email, linkedToName, onUnlink, isSelf = false }) => {
    const classes = useStyles();
    const [presentAlert] = useIonAlert();

    const name = linkedToName || 'this person';

    const confirmUnlink = () => {
        presentAlert({
            header: 'Unlink this member?',
            message: isSelf
                ? 'This stops syncing your account details to this care map. Your current name, photo, and contact info stay as they are now.'
                : `This stops syncing ${name}'s account details to this care map. Their current name, photo, and contact info stay as they are now.`,
            cssClass: 'drawer-alert',
            buttons: [
                { text: 'Cancel', role: 'cancel' },
                { text: 'Unlink', handler: () => onUnlink() }
            ]
        });
    };

    return (
        <div className={classes.root} data-id="linked-member-email-field">
            <div className={classes.readOnlyField}>
                <TextField
                    name="linked-email"
                    label="Email"
                    value={email}
                    onInput={() => {}}
                />
            </div>
            <div className={classes.advisory}>
                <Text type="detail">
                    {isSelf ? (
                        <>Linked to your account (<span className="mp-mask">{email}</span>). Edits here sync to your account and any care map you're in.</>
                    ) : (
                        <>Linked to <span className="mp-mask">{name}</span>'s account (<span className="mp-mask">{email}</span>). Edits here sync to their account and any care map they're in.</>
                    )}
                </Text>
                <div className={classes.actions}>
                    <IonButton fill="clear" size="small" data-id="unlink-member-button" onClick={confirmUnlink}>
                        Unlink
                    </IonButton>
                </div>
            </div>
        </div>
    );
};
